import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { formatMoney, LOCALE_TAGS, type PublicSettings } from '@shop/shared';
import { api } from './api';
import { useLocale, useT } from './i18n';

const SettingsContext = createContext<PublicSettings | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const locale = useLocale();
  const t = useT();
  const { data, error, refetch } = useQuery({
    queryKey: ['settings', locale],
    queryFn: () => api<PublicSettings>('/api/store/settings'),
    staleTime: 60_000,
    // Keep showing the previous language's settings while the new ones load.
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (data?.store.accentColor) document.documentElement.style.setProperty('--brand-accent', data.store.accentColor);
  }, [data?.store.accentColor]);

  if (error && !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-bone px-6 text-center">
        <p className="font-display text-3xl">{t.offline.title}</p>
        <p className="max-w-sm text-stone">{t.offline.body}</p>
        <button onClick={() => refetch()} className="btn btn-primary h-11 px-6">
          {t.common.tryAgain}
        </button>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-bone" aria-busy="true" />;
  return <SettingsContext.Provider value={data}>{children}</SettingsContext.Provider>;
}

export function useSettings(): PublicSettings {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used inside SettingsProvider');
  return ctx;
}

export function useMoney() {
  const { store } = useSettings();
  const locale = useLocale();
  return (minor: number) => formatMoney(minor, store.currency, LOCALE_TAGS[locale]);
}
